import { withShopBlockUuids } from "../blockIdentity";
import {
    SHOP_DOCUMENT_ROLE_OPTION,
    SHOP_EDITOR_ORIGIN_OPTION,
    SHOP_EDITOR_ORIGIN_USER,
    SHOP_GRID_SLOT_KEY,
} from "../shopBlocks";
import { shopStorefrontMessage } from "../storefrontCatalog";
import type { ShopStorefrontMessageKey } from "../storefrontCatalog";
import type { ShopTemplateBlock } from "../types";
import { MORROW_FOOTER_PAGE_TEMPLATE_KEYS } from "./morrowPageLinks";

export const MORROW_FOOTER_LAYOUT_STYLES = [
    "editorial",
    "columns",
    "centered",
    "compact",
    "split",
    "stacked",
    "showcase",
    "minimal",
] as const;

export const MORROW_FOOTER_SKELETON_VERSION = "2";

export type MorrowFooterLayoutStyle = typeof MORROW_FOOTER_LAYOUT_STYLES[number];

export type MorrowFooterGridPlacement = {
    h: number;
    w: number;
    x: number;
    y: number;
};

const footerRoles = [
    "footerCopy",
    "footerCatalog",
    "footerSupport",
    "footerSocial",
    "footerDivider",
    "footerBottom",
] as const;

type MorrowFooterRole = typeof footerRoles[number];

export const MORROW_FOOTER_LAYOUT_PLACEMENTS: Record<
    MorrowFooterLayoutStyle,
    Record<MorrowFooterRole, MorrowFooterGridPlacement>
> = {
    editorial: {
        footerCopy: { x: 1, y: 1, w: 4, h: 3 },
        footerCatalog: { x: 6, y: 1, w: 2, h: 3 },
        footerSupport: { x: 8, y: 1, w: 2, h: 3 },
        footerSocial: { x: 10, y: 1, w: 3, h: 3 },
        footerDivider: { x: 1, y: 4, w: 12, h: 1 },
        footerBottom: { x: 1, y: 5, w: 12, h: 1 },
    },
    columns: {
        footerCopy: { x: 1, y: 1, w: 3, h: 3 },
        footerCatalog: { x: 4, y: 1, w: 3, h: 3 },
        footerSupport: { x: 7, y: 1, w: 3, h: 3 },
        footerSocial: { x: 10, y: 1, w: 3, h: 3 },
        footerDivider: { x: 1, y: 4, w: 12, h: 1 },
        footerBottom: { x: 1, y: 5, w: 12, h: 1 },
    },
    centered: {
        footerCopy: { x: 3, y: 1, w: 8, h: 2 },
        footerCatalog: { x: 2, y: 3, w: 3, h: 2 },
        footerSupport: { x: 5, y: 3, w: 3, h: 2 },
        footerSocial: { x: 8, y: 3, w: 3, h: 2 },
        footerDivider: { x: 1, y: 5, w: 12, h: 1 },
        footerBottom: { x: 3, y: 6, w: 8, h: 1 },
    },
    compact: {
        footerCopy: { x: 1, y: 1, w: 4, h: 2 },
        footerCatalog: { x: 5, y: 1, w: 2, h: 2 },
        footerSupport: { x: 7, y: 1, w: 3, h: 2 },
        footerSocial: { x: 10, y: 1, w: 3, h: 2 },
        footerDivider: { x: 1, y: 3, w: 12, h: 1 },
        footerBottom: { x: 1, y: 4, w: 12, h: 1 },
    },
    split: {
        footerCopy: { x: 1, y: 1, w: 6, h: 4 },
        footerCatalog: { x: 7, y: 1, w: 3, h: 2 },
        footerSupport: { x: 10, y: 1, w: 3, h: 2 },
        footerSocial: { x: 7, y: 3, w: 6, h: 2 },
        footerDivider: { x: 1, y: 5, w: 12, h: 1 },
        footerBottom: { x: 1, y: 6, w: 12, h: 1 },
    },
    stacked: {
        footerCopy: { x: 1, y: 1, w: 8, h: 2 },
        footerSocial: { x: 9, y: 1, w: 4, h: 2 },
        footerDivider: { x: 1, y: 3, w: 12, h: 1 },
        footerCatalog: { x: 1, y: 4, w: 6, h: 2 },
        footerSupport: { x: 7, y: 4, w: 6, h: 2 },
        footerBottom: { x: 1, y: 6, w: 12, h: 1 },
    },
    showcase: {
        footerCopy: { x: 1, y: 1, w: 12, h: 2 },
        footerCatalog: { x: 1, y: 3, w: 3, h: 3 },
        footerSupport: { x: 4, y: 3, w: 3, h: 3 },
        footerSocial: { x: 8, y: 3, w: 5, h: 3 },
        footerDivider: { x: 1, y: 6, w: 12, h: 1 },
        footerBottom: { x: 1, y: 7, w: 12, h: 1 },
    },
    minimal: {
        footerCopy: { x: 1, y: 1, w: 5, h: 2 },
        footerCatalog: { x: 6, y: 1, w: 2, h: 2 },
        footerSupport: { x: 8, y: 1, w: 2, h: 2 },
        footerSocial: { x: 10, y: 1, w: 3, h: 2 },
        footerDivider: { x: 1, y: 3, w: 12, h: 1 },
        footerBottom: { x: 1, y: 4, w: 12, h: 1 },
    },
};

const rootLayoutOptions: Record<MorrowFooterLayoutStyle, Record<string, unknown>> = {
    editorial: { minHeight: "290px", paddingBottom: "46px", paddingTop: "46px" },
    columns: { minHeight: "280px", paddingBottom: "42px", paddingTop: "42px" },
    centered: { minHeight: "340px", paddingBottom: "50px", paddingTop: "50px" },
    compact: { minHeight: "220px", paddingBottom: "30px", paddingTop: "30px" },
    split: { minHeight: "330px", paddingBottom: "48px", paddingTop: "48px" },
    stacked: { minHeight: "340px", paddingBottom: "48px", paddingTop: "48px" },
    showcase: { minHeight: "390px", paddingBottom: "54px", paddingTop: "54px" },
    minimal: { minHeight: "210px", paddingBottom: "28px", paddingTop: "28px" },
};

function userOptions(options: Record<string, unknown>): Record<string, unknown> {
    return {
        ...options,
        [SHOP_EDITOR_ORIGIN_OPTION]: SHOP_EDITOR_ORIGIN_USER,
    };
}

function localizedTextBlock(
    languageCode: string,
    messageKey: ShopStorefrontMessageKey,
    options: Record<string, unknown>,
    content: Record<string, unknown> = {},
): ShopTemplateBlock {
    return {
        type: "text",
        content: {
            text: shopStorefrontMessage(languageCode, messageKey),
            ...content,
        },
        defaultMessageKeys: { "content.text": messageKey },
        options: userOptions(options),
    };
}

function localizedIconBlock(
    languageCode: string,
    messageKey: ShopStorefrontMessageKey,
    icon: string,
    href: string,
    textColor: string,
): ShopTemplateBlock {
    return {
        type: "icon",
        content: {
            href,
            icon,
            label: shopStorefrontMessage(languageCode, messageKey),
        },
        defaultMessageKeys: { "content.label": messageKey },
        options: userOptions({
            backgroundColor: "rgba(255,255,255,.08)",
            borderColor: "rgba(255,255,255,.18)",
            borderRadius: "999px",
            borderWidth: "1px",
            iconSize: "20px",
            padding: "11px",
            textColor,
        }),
    };
}

function footerColumn(
    role: MorrowFooterRole,
    children: ShopTemplateBlock[],
    options: Record<string, unknown> = {},
): ShopTemplateBlock {
    return {
        type: "column",
        options: userOptions({
            [SHOP_DOCUMENT_ROLE_OPTION]: role,
            ...options,
        }),
        slots: { content: children },
    };
}

function defaultRoleBlock(role: MorrowFooterRole, languageCode: string): ShopTemplateBlock {
    if (role === "footerDivider") {
        return {
            type: "separator",
            options: userOptions({
                backgroundColor: "rgba(246,242,233,.25)",
                minHeight: "1px",
                [SHOP_DOCUMENT_ROLE_OPTION]: role,
            }),
        };
    }

    if (role === "footerCopy") {
        return footerColumn(role, [
            localizedTextBlock(languageCode, "template.morrow.brand", {
                [SHOP_DOCUMENT_ROLE_OPTION]: "footerBrand",
                fontSize: "34px",
                fontWeight: "850",
                letterSpacing: "-.05em",
                runtimeText: "shopName",
            }),
            localizedTextBlock(languageCode, "template.morrow.footer.tagline", {
                [SHOP_DOCUMENT_ROLE_OPTION]: "footerTagline",
                fontSize: "20px",
                lineHeight: "1.4",
            }),
        ], { gap: "18px" });
    }

    if (role === "footerCatalog") {
        return footerColumn(role, [
            localizedTextBlock(languageCode, "template.morrow.footer.catalog", {
                [SHOP_DOCUMENT_ROLE_OPTION]: "footerCatalogTitle",
                fontSize: "12px",
                fontWeight: "800",
                letterSpacing: ".12em",
                marginBottom: "6px",
                minHeight: "34px",
                textTransform: "uppercase",
            }),
            localizedTextBlock(languageCode, "template.morrow.footer.showProducts", { fontSize: "13px", lineHeight: "1.45", opacity: ".76" }, { href: "/" }),
            localizedTextBlock(languageCode, "template.morrow.footer.giftCards", { fontSize: "13px", lineHeight: "1.45", morrowPageTemplateKey: MORROW_FOOTER_PAGE_TEMPLATE_KEYS.giftCards, opacity: ".76" }),
            localizedTextBlock(languageCode, "template.morrow.footer.sustainability", { fontSize: "13px", lineHeight: "1.45", morrowPageTemplateKey: MORROW_FOOTER_PAGE_TEMPLATE_KEYS.sustainability, opacity: ".76" }),
        ], { gap: "10px" });
    }

    if (role === "footerSupport") {
        return footerColumn(role, [
            localizedTextBlock(languageCode, "template.morrow.footer.customerService", {
                [SHOP_DOCUMENT_ROLE_OPTION]: "footerSupportTitle",
                fontSize: "12px",
                fontWeight: "800",
                letterSpacing: ".12em",
                marginBottom: "6px",
                minHeight: "34px",
                textTransform: "uppercase",
            }),
            localizedTextBlock(languageCode, "template.morrow.footer.myAccount", {
                [SHOP_DOCUMENT_ROLE_OPTION]: "footerAccountLink",
                fontSize: "13px",
                lineHeight: "1.45",
                opacity: ".76",
            }, { href: "/account" }),
            localizedTextBlock(languageCode, "template.morrow.footer.delivery", { fontSize: "13px", lineHeight: "1.45", morrowPageTemplateKey: MORROW_FOOTER_PAGE_TEMPLATE_KEYS.delivery, opacity: ".76" }),
            localizedTextBlock(languageCode, "template.morrow.footer.returns", { fontSize: "13px", lineHeight: "1.45", morrowPageTemplateKey: MORROW_FOOTER_PAGE_TEMPLATE_KEYS.returns, opacity: ".76" }),
        ], { gap: "10px" });
    }

    if (role === "footerSocial") {
        return footerColumn(role, [
            localizedTextBlock(languageCode, "template.morrow.footer.followUs", {
                [SHOP_DOCUMENT_ROLE_OPTION]: "footerSocialTitle",
                fontSize: "12px",
                fontWeight: "800",
                letterSpacing: ".12em",
                textTransform: "uppercase",
            }),
            footerColumn("footerSocial", [
                localizedIconBlock(languageCode, "block.instagram.label", "instagram", "https://instagram.com", "#FFB36B"),
                localizedIconBlock(languageCode, "block.facebook.label", "facebook", "https://facebook.com", "#DCEBFA"),
                localizedIconBlock(languageCode, "template.morrow.footer.email", "mail", "mailto:hello@example.com", "#FFFFFF"),
            ], {
                contentLayout: "inline",
                [SHOP_DOCUMENT_ROLE_OPTION]: "footerSocialLinks",
                gap: "10px",
            }),
        ], { gap: "14px" });
    }

    return footerColumn(role, [
        localizedTextBlock(languageCode, "template.morrow.footer.copyright", {
            fontSize: "12px",
            fontWeight: "700",
            marginRight: "auto",
            opacity: ".68",
        }),
        localizedTextBlock(languageCode, "template.morrow.footer.terms", { fontSize: "13px", lineHeight: "1.45", morrowPageTemplateKey: MORROW_FOOTER_PAGE_TEMPLATE_KEYS.terms, opacity: ".76" }),
        localizedTextBlock(languageCode, "template.morrow.footer.privacy", { fontSize: "13px", lineHeight: "1.45", morrowPageTemplateKey: MORROW_FOOTER_PAGE_TEMPLATE_KEYS.privacy, opacity: ".76" }),
        localizedTextBlock(languageCode, "template.morrow.footer.cookies", { fontSize: "13px", lineHeight: "1.45", morrowPageTemplateKey: MORROW_FOOTER_PAGE_TEMPLATE_KEYS.cookies, opacity: ".76" }),
    ], { contentLayout: "inline", gap: "18px" });
}

function roleLayoutOptions(layout: MorrowFooterLayoutStyle, role: MorrowFooterRole): Record<string, unknown> {
    const placement = MORROW_FOOTER_LAYOUT_PLACEMENTS[layout][role];
    const centered = layout === "centered" || layout === "showcase";
    const socialAlignment = layout === "columns" ? "left" : centered || layout === "split" ? "center" : "right";

    if (role === "footerDivider") {
        return {
            gridH: placement.h,
            gridW: placement.w,
            gridX: placement.x,
            gridY: placement.y,
        };
    }

    return {
        alignment: role === "footerSocial" ? socialAlignment : centered ? "center" : "left",
        contentLayout: role === "footerBottom" ? "inline" : undefined,
        gap: role === "footerCopy" ? "18px" : role === "footerSocial" ? "14px" : role === "footerBottom" ? "18px" : "10px",
        gridH: placement.h,
        gridW: placement.w,
        gridX: placement.x,
        gridY: placement.y,
        verticalAlign: layout === "compact" || layout === "minimal" || role === "footerBottom" ? "center" : "top",
    };
}

function patchNestedFooterAlignment(
    block: ShopTemplateBlock,
    layout: MorrowFooterLayoutStyle,
    parentRole: MorrowFooterRole,
): ShopTemplateBlock {
    const centered = layout === "centered" || layout === "showcase";
    const socialAlignment = layout === "columns" ? "left" : centered || layout === "split" ? "center" : "right";
    const documentRole = block.options?.[SHOP_DOCUMENT_ROLE_OPTION];
    const defaultTextMessageKey = block.defaultMessageKeys?.["content.text"];
    const isCopyright = block.defaultMessageKeys?.["content.text"] === "template.morrow.footer.copyright";
    const options: Record<string, unknown> = {
        ...(block.options ?? {}),
        [SHOP_EDITOR_ORIGIN_OPTION]: SHOP_EDITOR_ORIGIN_USER,
    };

    if (
        block.type === "column"
        && documentRole === "footerSocialLinks"
    ) {
        delete options.builderAutoWrapped;
        delete options.builderTextColumn;
    }

    if (defaultTextMessageKey === "template.morrow.footer.catalog") {
        options[SHOP_DOCUMENT_ROLE_OPTION] = "footerCatalogTitle";
        options.minHeight ??= "34px";
    }

    if (defaultTextMessageKey === "template.morrow.footer.customerService") {
        options[SHOP_DOCUMENT_ROLE_OPTION] = "footerSupportTitle";
        options.minHeight ??= "34px";
    }

    if (parentRole === "footerSocial" && documentRole === "footerSocialTitle") {
        options.textAlign = socialAlignment;
    }

    if (parentRole === "footerSocial" && documentRole === "footerSocialLinks") {
        options.alignment = socialAlignment;
    }

    if (parentRole === "footerBottom" && isCopyright) {
        if (centered) {
            delete options.marginRight;
        } else {
            options.marginRight = "auto";
        }
    }

    const slots = block.slots
        ? Object.fromEntries(Object.entries(block.slots).map(([slotKey, children]) => [
            slotKey,
            children.map((child) => patchNestedFooterAlignment(child, layout, parentRole)),
        ]))
        : undefined;
    const children = block.children?.map((child) => patchNestedFooterAlignment(child, layout, parentRole));

    return {
        ...block,
        options,
        ...(slots ? { slots } : {}),
        ...(children ? { children } : {}),
    };
}

function patchRoleLayout(block: ShopTemplateBlock, layout: MorrowFooterLayoutStyle, role: MorrowFooterRole): ShopTemplateBlock {
    const options: Record<string, unknown> = {
        ...(block.options ?? {}),
        ...roleLayoutOptions(layout, role),
        [SHOP_DOCUMENT_ROLE_OPTION]: role,
    };

    if (block.type === "column") {
        delete options.builderAutoWrapped;
        delete options.builderTextColumn;
    }

    for (const [key, value] of Object.entries(options)) {
        if (value === undefined) {
            delete options[key];
        }
    }

    return patchNestedFooterAlignment({ ...block, options }, layout, role);
}

export function normalizeMorrowFooterLayoutStyle(value: unknown): MorrowFooterLayoutStyle {
    return typeof value === "string" && (MORROW_FOOTER_LAYOUT_STYLES as readonly string[]).includes(value)
        ? value as MorrowFooterLayoutStyle
        : "editorial";
}

function isMorrowFooterRole(value: unknown): value is MorrowFooterRole {
    return typeof value === "string" && (footerRoles as readonly string[]).includes(value);
}

function normalizeSavedMorrowFooterChild(block: ShopTemplateBlock): ShopTemplateBlock {
    const options: Record<string, unknown> = { ...(block.options ?? {}) };
    const documentRole = options[SHOP_DOCUMENT_ROLE_OPTION];
    const defaultTextMessageKey = block.defaultMessageKeys?.["content.text"];

    if (block.type === "column" && documentRole === "footerSocialLinks") {
        delete options.builderAutoWrapped;
        delete options.builderTextColumn;
    }

    if (defaultTextMessageKey === "template.morrow.footer.catalog") {
        options[SHOP_DOCUMENT_ROLE_OPTION] = "footerCatalogTitle";
        options.minHeight ??= "34px";
    }

    if (defaultTextMessageKey === "template.morrow.footer.customerService") {
        options[SHOP_DOCUMENT_ROLE_OPTION] = "footerSupportTitle";
        options.minHeight ??= "34px";
    }

    const slots = block.slots
        ? Object.fromEntries(Object.entries(block.slots).map(([slotKey, children]) => [
            slotKey,
            children.map(normalizeSavedMorrowFooterChild),
        ]))
        : undefined;
    const children = block.children?.map(normalizeSavedMorrowFooterChild);

    return {
        ...block,
        options,
        ...(slots ? { slots } : {}),
        ...(children ? { children } : {}),
    };
}

/**
 * Removes the obsolete auto-wrapper metadata from persisted Morrow footer
 * structure without recreating deleted roles or replacing authored content.
 */
export function normalizeMorrowFooterLayoutStructure(footer: ShopTemplateBlock): ShopTemplateBlock {
    if (footer.type !== "richContent" || footer.options?.morrowRegion !== "footer") {
        return footer;
    }

    const layout = normalizeMorrowFooterLayoutStyle(footer.options.footerLayoutStyle);
    const currentItems = footer.slots?.[SHOP_GRID_SLOT_KEY];
    const normalizedItems = currentItems?.map((block) => {
        const role = block.options?.[SHOP_DOCUMENT_ROLE_OPTION];

        if (!isMorrowFooterRole(role)) {
            return block;
        }

        const options: Record<string, unknown> = { ...(block.options ?? {}) };
        const legacyAutoWrapped = block.type === "column"
            && (options.builderAutoWrapped === true || options.builderTextColumn === true);

        if (block.type === "column") {
            delete options.builderAutoWrapped;
            delete options.builderTextColumn;
        }

        if (legacyAutoWrapped) {
            options.gridH = MORROW_FOOTER_LAYOUT_PLACEMENTS[layout][role].h;
        }

        return normalizeSavedMorrowFooterChild({ ...block, options });
    });

    return {
        ...footer,
        ...(normalizedItems ? {
            slots: {
                ...(footer.slots ?? {}),
                [SHOP_GRID_SLOT_KEY]: normalizedItems,
            },
        } : {}),
    };
}

export function applyMorrowFooterLayoutSkeleton(
    footer: ShopTemplateBlock,
    layout: MorrowFooterLayoutStyle,
    languageCode: string,
): ShopTemplateBlock {
    if (footer.type !== "richContent" || footer.options?.morrowRegion !== "footer") {
        return footer;
    }

    const currentItems = footer.slots?.[SHOP_GRID_SLOT_KEY] ?? [];
    const usedIndexes = new Set<number>();
    const skeletonItems = footerRoles.map((role) => {
        const currentIndex = currentItems.findIndex((item, index) => (
            !usedIndexes.has(index) && item.options?.[SHOP_DOCUMENT_ROLE_OPTION] === role
        ));
        const item = currentIndex >= 0 ? currentItems[currentIndex] : defaultRoleBlock(role, languageCode);

        if (currentIndex >= 0) {
            usedIndexes.add(currentIndex);
        }

        return patchRoleLayout(item, layout, role);
    });
    const customItems = currentItems.filter((_, index) => !usedIndexes.has(index));

    return normalizeMorrowFooterLayoutStructure(withShopBlockUuids({
        ...footer,
        options: {
            ...(footer.options ?? {}),
            ...rootLayoutOptions[layout],
            footerLayoutStyle: layout,
            footerLayoutSkeletonVersion: MORROW_FOOTER_SKELETON_VERSION,
        },
        slots: {
            ...(footer.slots ?? {}),
            [SHOP_GRID_SLOT_KEY]: [...skeletonItems, ...customItems],
        },
    }));
}
